/**
 * CodeWidget component.
 *
 * A plain text widget that, when first added with no value, fills itself
 * in with a randomly generated code in the format "ABCD-1234-EFGH-5678".
 *
 * @module components/Widgets/CodeWidget/CodeWidget
 */

import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Input } from 'semantic-ui-react';

import Icon from '@plone/volto/components/theme/Icon/Icon';
import FormFieldWrapper from '@plone/volto/components/manage/Widgets/FormFieldWrapper';

import clearSVG from '@plone/volto/icons/clear.svg';

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

const randomLetters = (count) =>
  Array.from(
    { length: count },
    () => LETTERS[Math.floor(Math.random() * LETTERS.length)],
  ).join('');

const randomDigits = (count) =>
  Array.from({ length: count }, () => Math.floor(Math.random() * 10)).join('');

const generateCode = () =>
  `${randomLetters(4)}-${randomDigits(4)}-${randomLetters(4)}-${randomDigits(4)}`;

const CodeWidget = (props) => {
  const {
    id,
    value,
    onChange,
    onBlur,
    onClick,
    minLength,
    maxLength,
    placeholder,
    isDisabled,
    focus,
    required,
    error,
    resettable,
  } = props;

  const ref = useRef();

  useEffect(() => {
    if (focus) {
      ref.current.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If no value is set yet (e.g. a brand new gift card), default it to a
  // freshly generated code. Only runs once on mount so that explicitly
  // clearing the field later isn't immediately overridden.
  useEffect(() => {
    if (!value) {
      onChange(id, generateCode());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = () => onChange(id, null);

  return (
    <FormFieldWrapper {...props} className="text">
      <div className="date-time-widget-wrapper text-widget-wrapper">
        <Input
          id={`field-${id}`}
          name={id}
          type="text"
          value={value || ''}
          disabled={isDisabled}
          placeholder={placeholder}
          onChange={({ target }) =>
            onChange(id, target.value === '' ? undefined : target.value)
          }
          ref={ref}
          onBlur={({ target }) =>
            onBlur(id, target.value === '' ? undefined : target.value)
          }
          onClick={() => onClick()}
          minLength={minLength || null}
          maxLength={maxLength || null}
          aria-required={required ? 'true' : undefined}
          aria-invalid={error?.length > 0 ? 'true' : undefined}
        />
        {resettable && value && (
          <button
            type="button"
            disabled={isDisabled}
            onClick={onReset}
            className="item ui noborder button"
            aria-label="Clear"
          >
            <Icon name={clearSVG} size="24px" className="close" />
          </button>
        )}
      </div>
    </FormFieldWrapper>
  );
};

CodeWidget.propTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.arrayOf(PropTypes.string),
  value: PropTypes.string,
  focus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  minLength: PropTypes.number,
  maxLength: PropTypes.number,
  wrapped: PropTypes.bool,
  placeholder: PropTypes.string,
  resettable: PropTypes.bool,
};

CodeWidget.defaultProps = {
  description: null,
  required: false,
  error: [],
  value: null,
  onChange: () => {},
  onBlur: () => {},
  onClick: () => {},
  onEdit: null,
  onDelete: null,
  focus: false,
  minLength: null,
  maxLength: null,
  resettable: true,
};

export default CodeWidget;
